import { useState } from "react";
import { Link, useLoaderData } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, MapPin } from "lucide-react";
import type { RetreatDetail, RetreatSummary, RetreatsResponse } from "@/lib/retreats";
import RetreatLink from "@/components/retreats/RetreatLink";
import { Blue2Page, Part } from "../Page";
import { CLUB, Draw, EASE, HEART, HOME, Head, Ink, VIEW } from "../bits";
import { LiveDates } from "../LiveDates";
import "../SectionsA.css";
import "./Retreats.css";

/* Retreats in the blue-2 style. Everything shown comes from the retreats API
   (the same loader as /retreats); retreats link to their existing detail pages,
   which handle registration and the waitlist. */

// The list endpoint returns full retreat records, so the detail fields are usually present.
type Retreat = RetreatSummary & Partial<Pick<RetreatDetail, "included" | "details" | "projects" | "priceTiers" | "faq" | "travelInfo" | "sponsors">>;

/* Retreat dates are stored as calendar days, so format them in UTC. */
const fmt = (iso: string, o: Intl.DateTimeFormatOptions) => new Date(iso).toLocaleDateString("en-US", { timeZone: "UTC", ...o });

function dateRange(start: string, end: string) {
  const sameMonth = fmt(start, { month: "long", year: "numeric" }) === fmt(end, { month: "long", year: "numeric" });
  return sameMonth
    ? `${fmt(start, { month: "long" })} ${fmt(start, { day: "numeric" })}–${fmt(end, { day: "numeric" })}, ${fmt(end, { year: "numeric" })}`
    : `${fmt(start, { month: "long", day: "numeric" })} – ${fmt(end, { month: "long", day: "numeric" })}, ${fmt(end, { year: "numeric" })}`;
}

const money = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: cents % 100 ? 2 : 0 })}`;
/** Leading emoji in admin-entered labels ("🧥 Quilted Vest Project") reads as clutter in this design. */
const plain = (s: string) => s.replace(/^[\p{Extended_Pictographic}️‍\s]+/u, "");

const TICK = "M7 13 L11.5 17.5 L19.5 8";

/* ─── Header ─── */

const PHOTOS = [
  { src: "/img/chelsea.jpeg", alt: "Chelsea smiling in a bow-print sweater", caption: "can’t wait to sew with you", r: -6, tape: "soft" },
  { src: "/video/live-class.jpg", alt: "Chelsea at her cutting table with fabric spread out", caption: "fabric everywhere (as usual)", r: 4, tape: "pop" },
  { src: "/img/products/qs-mat.jpg", alt: "A tall stack of colorful folded fabric", caption: "pack the good stuff", r: -2, tape: "extra" },
] as const;

function Header({ next }: { next?: Retreat }) {
  return <div className="br-head">
    <div className="br-head-copy">
      <p className="br-kicker">In person · a few times a year</p>
      <h1 className="br-title" aria-label="Sew away: sewing retreats">
        <motion.span className="br-title-hand" aria-hidden="true" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.4, duration: 0.5, ease: EASE }}>sew</motion.span>
        <span className="br-title-word" aria-hidden="true">
          {"away".split("").map((c, i) => <motion.span key={i} initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
        </span>
      </h1>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.5, ease: EASE }}>
        <p className="br-hand">A few days away to sew, eat, and laugh with me.
          <svg viewBox="0 0 42 62" aria-hidden="true"><Draw d="M6 18 L32 4" delay={1.1} width={4.5} /><Draw d="M10 34 L38 30" delay={1.18} width={4.5} /><Draw d="M8 50 L30 56" delay={1.26} width={4.5} /></svg>
        </p>
        <p className="br-lede">A few times a year we gather in person to sew, shop, and slow down together — weekends full of creativity, community, and a little Chelsea-style fun.</p>
        <div className="br-actions">
          {next
            ? <><a href="#next" className="b2-btn b2-btn-yellow">See the next retreat <ArrowRight size={17} aria-hidden="true" /></a>
              <a href="#included" className="b2-btn b2-btn-ghost">What’s included</a></>
            : <Link to={`${HOME}#newsletter`} className="b2-btn b2-btn-yellow">Hear about the next one <ArrowRight size={17} aria-hidden="true" /></Link>}
        </div>
      </motion.div>
    </div>
    <div className="br-head-photos" aria-hidden="false">
      {PHOTOS.map((p, i) => <motion.figure key={p.src} className={`br-pol br-pol-${i}`}
        initial={{ opacity: 0, y: 40, rotate: p.r + 10 }} animate={{ opacity: 1, y: 0, rotate: p.r }} whileHover={{ y: -6, rotate: p.r / 2 }}
        transition={{ delay: 0.5 + i * 0.15, type: "spring", stiffness: 140, damping: 15 }}>
        <i className={`br-tape br-tape-${p.tape}`} aria-hidden="true" />
        <span><img src={p.src} alt={p.alt} /></span>
        <figcaption>{p.caption} <b aria-hidden="true">♥</b></figcaption>
      </motion.figure>)}
    </div>
  </div>;
}

/* ─── Upcoming ─── */

function Cover({ r }: { r: Retreat }) {
  if (r.heroImageUrl) return <div className="br-cover br-cover-photo"><img src={r.heroImageUrl} alt="" /></div>;
  const start = r.startDate, end = r.endDate;
  const span = fmt(start, { month: "short" }) === fmt(end, { month: "short" })
    ? `${fmt(start, { month: "short" })} ${fmt(start, { day: "numeric" })}–${fmt(end, { day: "numeric" })}`
    : `${fmt(start, { month: "short", day: "numeric" })} – ${fmt(end, { month: "short", day: "numeric" })}`;
  return <div className="br-cover" aria-hidden="true">
    <span className="br-cover-year">{fmt(end, { year: "numeric" })}</span>
    <span className="br-cover-place">{r.location.replace(/,\s*(Utah|UT)$/i, "")}</span>
    <span className="br-cover-dates">{span}</span>
    <svg className="br-cover-heart" viewBox="0 0 30 46"><Ink d={HEART} delay={0.9} width={3.2} /></svg>
  </div>;
}

function Status({ status }: { status: Retreat["status"] }) {
  return <span className={`br-status br-status-${status}`}>{status === "sold-out" ? "Sold out · waitlist open" : "Booking open"}</span>;
}

function Upcoming({ r, i }: { r: Retreat; i: number }) {
  const tiers = r.priceTiers ?? [];
  const sold = r.status === "sold-out";
  return <motion.article className="br-card" aria-labelledby={`br-card-${r.slug}`}
    initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: i * 0.1, duration: 0.6, ease: EASE }}>
    <div className="br-card-art">
      <i className="br-tape br-tape-pop" aria-hidden="true" />
      <Cover r={r} />
    </div>
    <div className="br-card-body">
      <div className="br-card-top">
        <Status status={r.status} />
        {r.subtitle && <p className="br-card-sub">{r.subtitle}</p>}
      </div>
      <h3 id={`br-card-${r.slug}`}>{r.title}</h3>
      <ul className="br-facts">
        <li><CalendarDays size={18} aria-hidden="true" />{dateRange(r.startDate, r.endDate)}</li>
        <li><MapPin size={18} aria-hidden="true" />{[r.venue, r.location].filter(Boolean).join(", ")}</li>
      </ul>
      {r.tagline && <p className="br-card-tag">{r.tagline}</p>}
      {tiers.length > 0 && <ul className="br-tiers" aria-label="Pricing">
        {tiers.map(t => <li key={t.name} className={`br-tier br-tier-${t.tierType}`}>
          <b>{t.tierType === "addon" ? "+ " : ""}{money(t.priceCents)}</b>
          <span>{t.name}</span>
          {t.description && <small>{t.description}</small>}
        </li>)}
      </ul>}
      <div className="br-card-cta">
        <RetreatLink slug={r.slug} to={`/blue-2/retreats/${r.slug}`} className="b2-btn b2-btn-blue">{sold ? "Join the waitlist" : "Save my spot"} <ArrowRight size={17} aria-hidden="true" /></RetreatLink>
        <RetreatLink slug={r.slug} to={`/blue-2/retreats/${r.slug}`} className="br-textlink">All the details</RetreatLink>
      </div>
    </div>
  </motion.article>;
}

function UpcomingSection({ upcoming }: { upcoming: Retreat[] }) {
  return <Part p="making">
    <section id="next" className="b2-sec br-next" aria-labelledby="br-next-title">
      <div className="b2-wrap">
        <div className="b2-row">
          <Head id="br-next-title" hand={upcoming.length > 1 ? "coming" : "the next"} word={upcoming.length > 1 ? "up" : "retreat"} tone="blue" />
          <div className="b2-row-aside">
            <p className="b2-note br-note">save the date ♥</p>
          </div>
        </div>
        {upcoming.length
          ? <div className="br-cards">{upcoming.map((r, i) => <Upcoming key={r.id} r={r} i={i} />)}</div>
          : <div className="br-empty">
            <p>No upcoming retreats. Subscribe to the newsletter to hear about the next one.</p>
            <Link to={`${HOME}#newsletter`} className="b2-btn b2-btn-blue">Get the newsletter <ArrowRight size={17} aria-hidden="true" /></Link>
          </div>}
      </div>
    </section>
  </Part>;
}

/* ─── What's included (from the next retreat) ─── */

function Included({ r }: { r: Retreat }) {
  const included = r.included ?? [];
  const project = r.projects?.[0];
  if (!included.length && !project) return null;
  return <Part p="club">
    <section id="included" className="b2-sec br-incl" aria-labelledby="br-incl-title">
      <div className="b2-wrap br-incl-grid">
        <div>
          <p className="b2-kicker br-incl-kicker">{r.title}</p>
          <Head id="br-incl-title" hand="what’s" word="included" tone="pink" />
          {included.length > 0 && <motion.ul className="br-checks" initial="off" whileInView="on" viewport={VIEW}>
            {included.map((x, i) => <li key={x.label}>
              <svg viewBox="0 0 26 26" aria-hidden="true"><rect x="2" y="2" width="22" height="22" rx="4" fill="none" stroke="currentColor" strokeWidth="2.4" />
                <motion.path d={TICK} fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round"
                  variants={{ off: { pathLength: 0, opacity: 0 }, on: { pathLength: 1, opacity: 1, transition: { delay: 0.3 + i * 0.15, duration: 0.4, opacity: { delay: 0.3 + i * 0.15, duration: 0.01 } } } }} /></svg>
              <span>{plain(x.label)}</span>
            </li>)}
          </motion.ul>}
        </div>
        {project && <motion.article className="br-project" aria-labelledby="br-project-title"
          initial={{ opacity: 0, y: 40, rotate: 4 }} whileInView={{ opacity: 1, y: 0, rotate: 1.5 }} viewport={VIEW} transition={{ type: "spring", stiffness: 110, damping: 16 }}>
          <i className="br-tape br-tape-pop" aria-hidden="true" />
          <p className="br-project-kicker">The project</p>
          <h3 id="br-project-title">{plain(project.name)}</h3>
          {project.description && <p className="br-project-desc">{project.description}</p>}
          {project.kitContents && project.kitContents.length > 0 && <>
            <p className="br-project-label">You’ll get</p>
            <ul>{project.kitContents.map(k => <li key={k.item}>{k.item}</li>)}</ul>
          </>}
        </motion.article>}
      </div>
    </section>
  </Part>;
}

/* ─── Good to know ─── */

function GoodToKnow({ r }: { r: Retreat }) {
  const details = r.details ?? [];
  const faq = r.faq ?? [];
  const [open, setOpen] = useState(0);
  if (!details.length && !faq.length && !r.travelInfo) return null;
  return <Part p="faves">
    <section className="b2-sec br-know" aria-labelledby="br-know-title">
      <div className="b2-wrap br-know-grid">
        <div className="br-know-side">
          <Head id="br-know-title" hand="good to" word="know" tone="blue" />
          {details.length > 0 && <ul className="br-details">
            {details.map(d => {
              const [k, ...v] = plain(d.label).split(":");
              return <li key={d.label}>{v.length ? <><small>{k}</small><span>{v.join(":").trim()}</span></> : <span>{k}</span>}</li>;
            })}
          </ul>}
          {r.travelInfo && <div className="br-travel">
            <p className="br-travel-label">Getting there</p>
            <p>{r.travelInfo}</p>
          </div>}
        </div>
        {faq.length > 0 && <div className="br-faq">
          <p className="b2-note br-faq-note">a few things you might wonder</p>
          {faq.map((f, i) => <details key={f.question} open={i === open} onToggle={e => { if ((e.currentTarget as HTMLDetailsElement).open) setOpen(i); }}>
            <summary>{f.question}<span aria-hidden="true" /></summary>
            <p>{f.answer}</p>
          </details>)}
        </div>}
      </div>
      {(r.sponsors?.length ?? 0) > 0 && <div className="b2-wrap br-sponsors">
        <p className="b2-note">with thanks to</p>
        <ul>{r.sponsors!.map(s => <li key={s.name}>
          {s.websiteUrl
            ? <a href={s.websiteUrl} target="_blank" rel="noreferrer">{s.logoUrl ? <img src={s.logoUrl} alt={s.name} /> : s.name}</a>
            : s.logoUrl ? <img src={s.logoUrl} alt={s.name} /> : s.name}
        </li>)}</ul>
      </div>}
    </section>
  </Part>;
}

/* ─── Past retreats ─── */

const FILLS = ["br-fill-a", "br-fill-b", "br-fill-c"];

function Past({ past }: { past: Retreat[] }) {
  if (!past.length) return null;
  return <Part p="brands">
    <section className="b2-sec br-past" aria-labelledby="br-past-title">
      <div className="b2-wrap">
        <Head id="br-past-title" hand="where we’ve" word="been" tone="pink" className="b2-head-center" />
        <ul className="br-wall">
          {past.map((r, i) => {
            const rot = [-4, 3, -2, 5][i % 4];
            return <motion.li key={r.id} initial={{ opacity: 0, y: 30, rotate: rot + 6 }} whileInView={{ opacity: 1, y: 0, rotate: rot }} whileHover={{ y: -6, rotate: 0 }} viewport={VIEW}
              transition={{ delay: i * 0.1, type: "spring", stiffness: 130, damping: 15 }}>
              <RetreatLink slug={r.slug} to={`/blue-2/retreats/${r.slug}`} className="br-wall-pol" aria-label={`${r.title}, ${dateRange(r.startDate, r.endDate)}: see the recap`}>
                <i className={`br-tape br-tape-${["soft", "pop", "extra"][i % 3]}`} aria-hidden="true" />
                <span className={`br-wall-img ${r.heroImageUrl ? "" : FILLS[i % FILLS.length]}`}>
                  {r.heroImageUrl ? <img src={r.heroImageUrl} alt="" /> : <b aria-hidden="true">{r.location.split(",")[0]}</b>}
                </span>
                <span className="br-wall-cap"><b>{r.title}</b>{fmt(r.startDate, { month: "short", day: "numeric" })} – {fmt(r.endDate, { month: "short", day: "numeric", year: "numeric" })}{r.venue ? ` · ${r.venue}` : ""}</span>
              </RetreatLink>
            </motion.li>;
          })}
        </ul>
      </div>
    </section>
  </Part>;
}

/* ─── Club nudge ─── */

function ClubNudge() {
  return <Part p="where">
    <section className="b2-sec br-online" aria-labelledby="br-online-title">
      <div className="b2-wrap br-online-grid">
        <div>
          <Head id="br-online-title" hand="can’t make it in person?" word="sew online" tone="pink" className="br-online-head" />
          <p className="b2-lede">Join the Loose Threads Club for two live project classes a month, with every replay included. Or catch me free on YouTube most Tuesdays.</p>
          <div className="br-actions">
            <Link to={CLUB} className="b2-btn b2-btn-yellow b2-btn-big">See the club <ArrowRight size={18} aria-hidden="true" /></Link>
          </div>
        </div>
        <LiveDates className="br-lives" />
      </div>
    </section>
  </Part>;
}

export function Blue2Retreats() {
  const { retreats } = useLoaderData() as RetreatsResponse;
  const all = retreats as Retreat[];
  const upcoming = all.filter(r => r.status === "open" || r.status === "sold-out");
  const past = all.filter(r => r.status === "past");
  const next = upcoming[0];
  return <Blue2Page current="retreats" tone="gold" header={<Header next={next} />}>
    <UpcomingSection upcoming={upcoming} />
    {next && <Included r={next} />}
    {next && <GoodToKnow r={next} />}
    <Past past={past} />
    <ClubNudge />
  </Blue2Page>;
}
